import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    DrawerBody,
    DrawerHeader,
    Grid,
    GridItem,
    VStack,
    Box,
    Input,
    Text,
    Drawer,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { UnitStateData } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { assertNonNull } from '../queues/utils/assert-non-null';
import { useUnitStatusColors } from '../configurable-colors-sounds/unit-status-colors';
import { useTransitionUnitStatusMutation } from '../unit-status/use-transition-unit-status-mutation';
import { alphabeticalStringSort } from '../common/sorting/sorts';
import { FR_EVENT_ROOT_PATH, FR_UNASSIGN_CURRENT_USER_UNIT_SUB_PATH } from '../router/constants';
import { useOpenFrEventDetails } from '../fr-event-details-page/hooks/use-open-fr-event-details';
import { useUnassignSelfMutation } from '../dispositions/hooks/use-unassign-self-mutation';
import { FR_EVENT_DETAILS_TABS } from '../fr-event-details-page/constants';

export const FrUnitStatusDrawer = observer(function FrUnitStatusDrawer(): React.ReactElement {
    const drawerControls = useDrawerControls();
    const closeDrawer = React.useMemo(() => {
        return () => {
            drawerControls.close();
        };
    }, [drawerControls]);
    return (
        <Drawer isOpen={true} size="lg" onClose={closeDrawer}>
            <FrUnitStatusDrawerContent closeDrawer={closeDrawer} />
        </Drawer>
    );
});

export type FrUnitStatusDrawerType = typeof FrUnitStatusDrawer;

const UnitStateButton = observer(function UnitStateButton(props: {
    unitStateData: UnitStateData;
    closeDrawer: () => void;
}) {
    const navigate = useNavigate();
    const openFrEventDetails = useOpenFrEventDetails();
    const { unitLevelDispositionsEnabled, additionalDispositionsEnabled } = useEnabledFeatures();
    const unitAssignment = useUnitAssignment();
    const isPrimaryUnit = !!unitAssignment?.isPrimary;
    const transitionUnitStatusMutation = useTransitionUnitStatusMutation();
    const assignedEventId = unitAssignment?.assignedEvent?.eventId;
    const unassignSelfMutation = useUnassignSelfMutation({
        onMutationSuccess: () => {
            openFrEventDetails(assignedEventId, FR_EVENT_DETAILS_TABS.NARRATIVE);
        },
    });

    const mutateArgs = {
        unitId: unitAssignment?.unitId,
        eventId: assignedEventId,
        unitStateData: props.unitStateData,
    } as const;

    const handleUnitStateButtonClick = () => {
        if (unitLevelDispositionsEnabled && !!assignedEventId && !props.unitStateData.isEventRelated) {
            props.closeDrawer();
            const shouldSkipDispositionForm = !additionalDispositionsEnabled && !isPrimaryUnit;

            if (shouldSkipDispositionForm) {
                unassignSelfMutation.mutate(unitAssignment?.unitId ? [unitAssignment.unitId] : []);
            } else {
                navigate(
                    `${FR_EVENT_ROOT_PATH}/${assignedEventId}/${FR_UNASSIGN_CURRENT_USER_UNIT_SUB_PATH}`
                );
            }
            return;
        }
        transitionUnitStatusMutation.mutateAsync(mutateArgs).then((transitioned) => {
            if (transitioned) {
                props.closeDrawer();
            }
        });
    };

    const unitStatusColor = useUnitStatusColors(
        props.unitStateData.id,
        props.unitStateData.unitStateGroup
    );

    return (
        <VStack
            w="100%"
            h="112px"
            alignItems="center"
            justifyContent="center"
            gap="6px"
            textAlign="center"
            borderRadius="md"
            backgroundColor={unitStatusColor.fill}
            cursor="pointer"
            tabIndex={0}
            padding="4px"
            onClick={() => {
                handleUnitStateButtonClick();
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    handleUnitStateButtonClick();
                }
            }}
        >
            <Text
                fontSize="30px"
                fontWeight="semibold"
                lineHeight={1}
                style={{ color: unitStatusColor.text }}
            >
                {props.unitStateData.abbreviation}
            </Text>
            <Text
                fontSize="12px"
                fontWeight="bold"
                lineHeight={1}
                style={{ color: unitStatusColor.text }}
            >
                {props.unitStateData.value.toUpperCase()}
            </Text>
        </VStack>
    );
});

function FrUnitStatusDrawerContent({
    closeDrawer,
}: {
    closeDrawer: () => void;
}): React.ReactElement {
    const unitAssignment = useUnitAssignment();
    const hasAssignedEvent = !!unitAssignment?.assignedEvent;
    const currentUnitStateId = unitAssignment?.unitStateData?.id;
    assertNonNull(currentUnitStateId, 'Unit state ID must be set');
    const { unitStateDataById } = useBootstrapState();
    const [searchText, setSearchText] = useState<string>('');

    const validNextUnitStateIds =
        unitStateDataById[currentUnitStateId].firstResponderTransitionToUnitStateIds;
    const validNextUnitStateDatas = validNextUnitStateIds
        .map((id) => {
            const unitStateData = unitStateDataById[id];
            if (!unitStateData) return;
            const searchText = `${unitStateData.abbreviation}|${unitStateData.value}`;
            return {
                ...unitStateData,
                searchText,
            };
        })
        .filter((usd) => {
            if (!usd) return false;
            return hasAssignedEvent ? true : !usd.isEventRelated;
        })
        .sort((a, b) => alphabeticalStringSort(a?.value ?? '', b?.value ?? ''));

    const filteredNextUnitStateDatas = useMemo(() => {
        return validNextUnitStateDatas.filter(
            (usd) => !!usd && usd.searchText.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [searchText, validNextUnitStateDatas]);

    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <>
            <DrawerHeader>
                <Box width="90%">
                    <Input
                        ref={inputRef}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        leftIcon="Search"
                    />
                </Box>
            </DrawerHeader>
            <DrawerBody>
                <Box
                    w="100%"
                    justifyContent="center"
                    alignItems="center"
                    data-testid="unit-status-drawer"
                >
                    <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                        {filteredNextUnitStateDatas.map((usd) =>
                            usd ? (
                                <GridItem key={usd.id}>
                                    <UnitStateButton
                                        unitStateData={usd}
                                        closeDrawer={closeDrawer}
                                    />
                                </GridItem>
                            ) : undefined
                        )}
                    </Grid>
                </Box>
            </DrawerBody>
        </>
    );
}
